/**
 * Base handler class for managing step functionality.
 * All specific step handlers should extend this class.
 */
export default class BaseStepHandler {
    /**
     * Create a new step handler
     * 
     * @param {string} stepId - The ID of the step this handler manages
     * @param {object} stepDefinition - The definition object from STEPS constant
     * @param {object} canvas - The STCCanvas instance
     * @param {object} stepManager - The StepManager instance
     * @param {string} side - The side this handler belongs to ('front' or 'back')
     */
    constructor(stepId, stepDefinition, canvas, stepManager, side) {
        this.stepId = stepId;
        this.definition = stepDefinition;
        this.canvas = canvas;
        this.stepManager = stepManager;
        this.side = side || 'front'; // Default to front if not specified
        
        this.elements = new Map(); // Store all elements managed by this handler
        this.isActive = false; // Whether this step is currently active
        this.isVisible = false; // Whether elements for this step are visible
        this.isInitialized = false; // Whether this handler has been initialized
        this.currentData = { enabled: stepDefinition.required };
        
        this.panelElement = null; // Will hold the panel DOM element
        this.listeners = []; // For tracking event listeners
    }
    
    /**
     * Initialize the handler
     * Called once when the handler is created
     * @returns {BaseStepHandler} this instance for chaining
     */
    initialize() {
        if (this.isInitialized) return this;
        
        // Find template elements that this step should manage
        this.loadTemplateElements();
        
        // Mark as initialized
        this.isInitialized = true;
        
        
        return this;
    }
    
    /**
     * Load elements from template that this step should manage
     */
    loadTemplateElements() {
        // Get element IDs from step definition
        const elementIds = this.definition.elements || [];
        
        // Load each element from canvas
        elementIds.forEach(id => {
            const element = this.canvas.getElement(id);
            if (element) {
                this.elements.set(id, element);
            }
        });
    }

    /**
     * Process template elements
     * Called when template is first loaded or when side changes
     * @returns {BaseStepHandler} this instance for chaining
     */
    processTemplate() {
        // Get step data
        const stepData = this.getStepData();
        
        // If we have data, update with it
        if (stepData && Object.keys(stepData).length > 0) {
            this.update(stepData);
        } else {
            // Otherwise, determine if elements should be shown by default
            if (this.definition.required === true) {
                this.show();
            } else {
                this.hide();
            }
        }
        
        return this;
    }

    /**
     * Get the price for this step
     * This method should be called by the price calculator
     * @returns {number} Step price
     */
    getPrice() {
        const stepData = this.getStepData();
        
        // Step is not enabled or completed
        if (!stepData || stepData.enabled !== true) {
            return 0;
        }
        
        // Use step-specific price calculation if available,
        // otherwise use price from step definition
        return stepData.price !== undefined ? 
            stepData.price : 
            (this.definition.price || 0);
    }

    /**
     * Get info content for this step
     * Can be overridden by subclasses to provide dynamic info content
     * 
     * @returns {object|null} Info content object or null if no info available
     */
    getInfoContent() {
        // Return info from step definition by default
        return this.definition.info || null;
    }
    
    /**
     * Activate this step
     * Called when the step becomes active (user clicks on it)
     * @returns {BaseStepHandler} this instance for chaining
     */
    activate() {

        this.isActive = true;
        
        let elements = this.getStepElements();

        elements.forEach((element) => {

            const allowedActions = this.definition.allowedActions || [];

            element.set({
                selectable: true,
                hasControls: allowedActions.includes('scale'),
                lockMovementX: !allowedActions.includes('move'),
                lockMovementY: !allowedActions.includes('move'),
                cornerStyle: 'circle',
                transparentCorners: false,
                cornerColor: '#0d6efd',
                cornerSize: 50,
                borderScaleFactor:10,
                padding: 10,
                evented: true
            });

        });
        
        this.canvas.canvas.requestRenderAll();
        
        return this;
    }
    
    /**
     * Deactivate this step
     * Called when another step becomes active
     * @returns {BaseStepHandler} this instance for chaining
     */
    deactivate() {

        this.isActive = false;
        
        let elements = this.getStepElements();

        elements.forEach((element) => {
            element.set({
                selectable: false,
                hasControls: false,
                lockMovementX: true,
                lockMovementY: true,
                evented: false
            });
        });
        
        this.canvas.canvas.requestRenderAll();
        
        return this;

    }
    
    /**
     * Update elements based on step data
     * @param {object} stepData - The current data for this step
     * @returns {BaseStepHandler} this instance for chaining
     */
    update(stepData) {

        // Store the current data for reference
        this.currentData = stepData;
        
        // Base implementation just handles visibility based on enabled flag
        if (stepData?.enabled === false) {
            this.hide();
        } else if (this.isVisible === false) {
            this.show();
        }
        
        return this;
    }
    
    /**
     * Handle side change
     * Called when switching between front and back
     * @param {boolean} isCurrentSide - Whether this handler's side is now active
     * @returns {BaseStepHandler} this instance for chaining
     */
    handleSideChange(isCurrentSide) {
        if (isCurrentSide) {
            // This handler's side is now active
            this.processTemplate();
        } else {
            // This handler's side is now inactive - hide elements
            this.hide();
        }
        
        return this;
    }
    
    /**
     * Show elements managed by this handler
     * @returns {BaseStepHandler} this instance for chaining
     */
    show() {
        const elementIds = this.definition.elements || [];
        elementIds.forEach(id => {
            const element = this.getElement(id);
            if (element) {
                element.set('visible', true);
            }
        });
        
        this.isVisible = true;
        this.canvas.canvas.requestRenderAll();
        
        return this;
    }
    
    /**
     * Hide elements managed by this handler
     * @returns {BaseStepHandler} this instance for chaining
     */
    hide() {
        const elementIds = this.definition.elements || [];
        elementIds.forEach(id => {
            const element = this.getElement(id);
            if (element) {
                element.set('visible', false);
            }
        });
        
        this.isVisible = false;
        this.canvas.canvas.requestRenderAll();
        
        return this;
    }
    
    /**
     * Create the panel element and add it to the container
     * @param {HTMLElement} container - Container to add the panel to
     * @returns {HTMLElement} The created panel element
     */
    createPanel(container) {
        // Create the panel element
        this.panelElement = document.createElement('div');
        this.panelElement.className = 'stc-step-panel';
        this.panelElement.innerHTML = this.renderPanelContent();
        
        // Add to container
        container.appendChild(this.panelElement);
        
        // Bind events
        this.bindPanelEvents();
        
        return this.panelElement;
    }
    
    /**
     * Render the panel content
     * Should be overridden by subclasses
     * @returns {string} HTML content for the panel
     */
    renderPanelContent() {
        // Each subclass should override this to provide its specific UI
        return `<div class="stc-step-default-panel">Default panel for ${this.stepId}</div>`;
    }
    
    /**
     * Bind events to panel elements
     * Should be overridden by subclasses
     */
    bindPanelEvents() {
        // Each subclass should override this to bind its specific events
    }
    
    /**
     * Update panel content without recreating it
     */
    updatePanel() {
        // Update panel content without full recreation
        if (this.panelElement) {
            // Unbind old events
            this.unbindPanelEvents();
            
            // Update content
            this.panelElement.innerHTML = this.renderPanelContent();
            
            // Rebind events
            this.bindPanelEvents();
        }
    }
    
    /**
     * Unbind all panel event listeners
     */
    unbindPanelEvents() {
        // Clean up event listeners
        this.listeners.forEach(({ element, event, handler }) => {
            if (element) {
                element.removeEventListener(event, handler);
            }
        });
        this.listeners = [];
    }
    
    /**
     * Helper to add event listener with tracking for cleanup
     * @param {HTMLElement} element - Element to add listener to
     * @param {string} event - Event name
     * @param {function} handler - Event handler function
     */
    addEventListener(element, event, handler) {
        if (!element) return;
        
        element.addEventListener(event, handler);
        this.listeners.push({ element, event, handler });
    }
    
    /**
     * Get current step data from manager
     * @returns {object} The current step data
     */
    getStepData() {
        return this.stepManager.getStepDataForSide(this.side, this.stepId) || {};
    }
    
    /**
     * Update step data in manager
     * @param {object} data - New step data
     * @param {boolean} notifyChanges - Whether to emit events
     */
    setStepData(data, notifyChanges = true) {
        // Update step data and notify manager
        this.stepManager.setStepDataForSide(this.side, this.stepId, data, notifyChanges);
    }
    
    /**
     * Get an element by ID
     * @param {string} id - Element ID
     * @returns {object} Fabric.js element
     */
    getElement(id) {
        return this.elements.get(id) || this.canvas.getElement(id);
    }

    /**
     * Get export data for this step
     * This collects data needed for cart/order processing
     * @returns {object} Export data
     */
    getStepExportData() {
        const stepData = this.getStepData();
        
        // Basic data all steps should have
        return {
          stepId: this.stepId,
          type: this.definition.type,
          enabled: stepData?.enabled !== false,
          position: this.getPositionData()
        };
    }

    getPositionData() {
        const stepData = this.getStepData();

        // Try to get position from stepData first
        // If not available, read from the actual element on canvas
        const elementId = this.definition?.elements?.[0];
        const element = elementId ? this.elements.get(elementId) : null;

        return {
          left: stepData?.left ?? element?.left,
          top: stepData?.top ?? element?.top,
          scaleX: stepData?.scaleX ?? element?.scaleX,
          scaleY: stepData?.scaleY ?? element?.scaleY
        };
    }
    
    /**
     * Get image reference data for export
     * Base implementation that image handlers will override
     * 
     * @returns {object|null} Image reference data or null if no image
     */
    getImageReferenceForExport() {
        return null;
    }

    /**
     * Get position data for all elements managed by this step
     * @returns {object} Position data for each element
     */
    getElementsPositionData() {
        const positionData = {};
        
        this.elements.forEach((element, id) => {
            if (!element) return;
            
            positionData[id] = {
                left: element.left,
                top: element.top,
                scaleX: element.scaleX,
                scaleY: element.scaleY,
                angle: element.angle,
                width: element.width,
                height: element.height,
                visible: element.visible
            };
        });
        
        return positionData;
    }

    /**
     * Get all elements managed by this step
     * @returns {Array} Array of elements
     */
    getStepElements() {
        // Convert the elements Map to an array of elements
        return Array.from(this.elements.values());
    }

    /**
     * Get template-formatted data for a specific element
     * Converts step handler data + canvas element data to template format
     * @param {string} elementId - The element ID to get data for
     * @returns {object|null} Template-formatted element data or null if element not found
     */
    getTemplateElementData(elementId) {
        // Get the canvas element
        const element = this.getElement(elementId);
        if (!element) {
            return null;
        }

        // Get current step data
        const stepData = this.getStepData() || {};

        // Base template structure for all elements
        const templateData = {
            id: elementId,
            type: this.getTemplateElementType(),
            data: this.getTemplateDataProperties(stepData, element),
            completed: this.isComplete(),
            left: element.left,
            top: element.top,
            scaleX: element.scaleX || 1,
            scaleY: element.scaleY || 1,
            angle: element.angle || 0
        };

        return templateData;
    }

    /**
     * Get the template element type based on step type
     * Override in subclasses for specific mappings
     * @returns {string} Template element type
     */
    getTemplateElementType() {
        // Default mapping from step type to template type
        const typeMapping = {
            'text': 'editable_text',
            'image': 'editable_image',
            'color': 'static_image',
            'select': 'static_image',
            'toggle': 'static_image'
        };

        return typeMapping[this.definition.type] || 'static_image';
    }

    /**
     * Get template data properties from step data and element
     * Override in subclasses for specific property mappings
     * @param {object} stepData - Current step data
     * @param {object} element - Canvas element
     * @returns {object} Template data properties
     */
    getTemplateDataProperties(stepData, element) {
        // Base implementation - include basic element properties
        return {
            left: element.left,
            top: element.top,
            scaleX: element.scaleX || 1,
            scaleY: element.scaleY || 1,
            angle: element.angle || 0,
            width: element.width,
            height: element.height,
            visible: element.visible !== false
        };
    }
    
    /**
     * Remove internal properties not needed for export
     * @param {object} data - Data to sanitize
     */
    sanitizeExportData(data) {
        // Internal properties to exclude from export
        const excludeProps = ['_lastSrc', '_isCustomImageLoading'];
        
        excludeProps.forEach(prop => {
            if (prop in data) {
                delete data[prop];
            }
        });
    }

    /**
     * Reset element to default state
     * This should be called when clearing a step
     * @returns {BaseStepHandler} this instance for chaining
     */
    resetToDefault() {
        // Clear step data in manager
        this.stepManager.clearStep(this.stepId);
        
        // Get element IDs from definition
        const elementIds = this.definition.elements || [];
        
        // Reset each element to default state
        elementIds.forEach(id => {
            const element = this.getElement(id);
            if (element) {
                // Get template element data
                const elementData = this.canvas.getElementData(id);
                if (elementData && elementData.data) {
                    // Restore original properties from template
                    element.set({
                        ...elementData.data,
                        selectable: false,
                        hasControls: false,
                        lockMovementX: true,
                        lockMovementY: true,
                        visible: this.definition.required !== false
                    });
                    
                    // For text elements, reset text content
                    if (element.type === 'text') {
                        element.set('text', elementData.data.text || '');
                    }
                    
                    // For image elements, reset to original source
                    if (element.type === 'image') {
                        if (elementData.data.src) {
                            element.setSrc(elementData.data.src);
                        }
                    }
                }
            }
        });
        
        // Additional cleanup specific to step type
        this.onReset();
        
        // Render changes
        this.canvas.canvas.requestRenderAll();
        
        return this;
    }

    /**
     * Step-specific reset logic
     * Override in subclasses to provide custom reset behavior
     */
    onReset() {
        // Base implementation does nothing
        // Subclasses can override this for custom cleanup
    }

    /**
     * Clear step data and reset elements
     */
    clear() {
        // Reset to default state
        this.resetToDefault();
        
        // Update panel UI
        this.updatePanel();
    }
    
    /**
     * Clean up handler resources
     * @returns {BaseStepHandler} this instance for chaining
     */
    dispose() {
        this.unbindPanelEvents();
        if (this.panelElement && this.panelElement.parentNode) {
            this.panelElement.parentNode.removeChild(this.panelElement);
        }
        this.panelElement = null;
        return this;
    }

    isComplete() {
        const stepData = this.getStepData() || {};
        return stepData.enabled;
    }

    /**
     * Determine what needs to be saved to WordPress
     * Each step handler decides what resources to persist
     * 
     * @returns {Promise<Array>} Array of save operations to perform
     */
    async getSaveOperations() {
        const operations = [];
        const stepData = this.getStepData();

        // Check if this step has any saveable content
        if (this.hasSaveableContent(stepData)) {
            const saveOps = await this.createSaveOperations(stepData);
            if (saveOps && saveOps.length > 0) {
                operations.push(...saveOps);
            }
        }

        return operations;
    }

    /**
     * Check if step has content that needs to be saved
     * 
     * @param {Object} stepData - Current step data
     * @returns {boolean} Whether step has saveable content
     */
    hasSaveableContent(stepData) {
        // Base implementation - override in subclasses
        return stepData && Object.keys(stepData).length > 0 && stepData.enabled !== false;
    }

    /**
     * Create save operations for this step's content
     * 
     * @param {Object} stepData - Current step data
     * @returns {Promise<Array>} Array of save operation objects
     */
    async createSaveOperations(stepData) {
        // Base implementation returns empty array - override in subclasses
        return [];
    }

    /**
     * Get priority for this step's save operations
     * Higher numbers = higher priority
     * 
     * @returns {number} Priority level (0-10)
     */
    getSaveOperationsPriority() {
        // Base implementation - override for critical steps
        return 5;
    }

    /**
     * Validate that save operations completed successfully
     * 
     * @param {Array} completedOperations - Operations that were processed
     * @returns {boolean} Whether this step's requirements were met
     */
    validateSaveOperations(completedOperations) {
        // Base implementation assumes success - override for validation
        return true;
    }

    /**
     * Prepare handler for preview generation
     * Called before high-resolution preview is generated
     * @returns {Promise<void>}
     */
    async prepareForPreview() {
        // Base implementation - override in subclasses
    }

    /**
     * Restore handler after preview generation
     * Called after high-resolution preview is generated
     * @returns {Promise<void>}
     */
    async restoreFromPreview() {
        // Base implementation - override in subclasses
    }

}